import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner'; // Assuming a common loading spinner
import { canAccessServiceDashboard, canAccessServiceModule } from '../../utils/accessControl';

const isOperationalServicePath = (pathname: string): boolean => {
  const normalizedPathname = pathname.replace(/\/+$/, '');
  return (
    normalizedPathname === '/app/service' ||
    normalizedPathname === '/app/dashboard' ||
    normalizedPathname.startsWith('/app/service/')
  );
};

const ServiceRouteGuard: React.FC = () => {
  const { currentUser, userProfile, claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Use a more integrated loading indicator if available, or a simple one
    return <LoadingSpinner />;
  }

  // 1. Check if user is logged in
  if (!currentUser) {
    // Redirect them to the login page, but save the current location they were trying to go to
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2. Check if email is verified (already handled by ProtectedRoute in App.tsx, but good practice to double-check if used independently)
  if (!currentUser.emailVerified) {
    return <Navigate to="/verify-email" state={{ email: currentUser.email }} replace />;
  }

  // 3. Check if user can access service module (onboarding + operational panel)
  if (!canAccessServiceModule(userProfile, claims)) {
    // Not a service provider, redirect to the main app page or profile
    console.warn('ServiceRouteGuard: User is not a service provider. Redirecting.');
    return <Navigate to="/app" replace />;
  }

  if (
    isOperationalServicePath(location.pathname) &&
    !canAccessServiceDashboard(userProfile, claims)
  ) {
    return <Navigate to="/app/service-profile?workspace=service" replace />;
  }

  // If all checks pass, render the child routes
  return <Outlet />; // Use Outlet to render nested routes defined within this guard in App.tsx
};

export default ServiceRouteGuard;
